import React, { useState, useEffect } from 'react';
import { cityAPI, stationAPI, routeAPI, journeyAPI, routePathAPI } from '../services/api';
import { City, Station, RouteResponse, Journey, RoutePathResponse } from '../types';
import './JourneyTesting.css';

interface JourneyTestingState {
  cities: City[];
  selectedCity: City | null;
  stations: Station[];
  sourceStation: Station | null;
  destinationStation: Station | null;
  route: RouteResponse | null;
  routePath: {
    results: Array<{
      fromStationId: string;
      toStationId: string;
      routePaths: number[][];
      success: boolean;
      errorMessage: string | null;
    }>;
  } | null;
  journey: Journey | null;
  isLoading: boolean;
  error: string | null;
}

const JourneyTesting: React.FC = () => {
  const [state, setState] = useState<JourneyTestingState>({
    cities: [],
    selectedCity: null,
    stations: [],
    sourceStation: null,
    destinationStation: null,
    route: null,
    routePath: null,
    journey: null,
    isLoading: false,
    error: null,
  });

  const [testLocation, setTestLocation] = useState({
    lat: '',
    lng: '',
    accuracy: '',
    speed: '',
  });

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [updateResponse, setUpdateResponse] = useState<any>(null);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isRequestCollapsed, setIsRequestCollapsed] = useState(false);

  // Load cities on component mount
  useEffect(() => {
    loadCities();
  }, []);

  // Load stations when city is selected
  useEffect(() => {
    if (state.selectedCity) {
      loadStations(state.selectedCity.id!);
    }
  }, [state.selectedCity]);

  const loadCities = async () => {
    try {
      const response = await cityAPI.getAll();
      setState(prev => ({ ...prev, cities: response.data }));
    } catch (error) {
      setState(prev => ({ ...prev, error: 'Failed to load cities' }));
    }
  };

  const loadStations = async (cityId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const response = await stationAPI.getByCity(cityId);
      setState(prev => ({ 
        ...prev, 
        stations: response.data,
        isLoading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to load stations',
        isLoading: false 
      }));
    }
  };

  const handleCitySelect = (city: City) => {
    setState(prev => ({
      ...prev,
      selectedCity: city,
      sourceStation: null,
      destinationStation: null,
      route: null,
      routePath: null,
      journey: null,
    }));
  };

  const handleStationSelect = (station: Station, type: 'source' | 'destination') => {
    if (type === 'source') {
      setState(prev => ({ ...prev, sourceStation: station }));
    } else {
      setState(prev => ({ ...prev, destinationStation: station }));
    }
  };

  const loadRoute = async () => {
    if (!state.sourceStation || !state.destinationStation) {
      setState(prev => ({ ...prev, error: 'Please select both source and destination stations' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Get route details
      const routeResponse = await routeAPI.quickRoute(
        state.sourceStation.stationCode,
        state.destinationStation.stationCode
      );
      
      // Get bulk route path coordinates for all station connections
      const bulkRoutePaths = await getBulkRoutePaths(routeResponse.data);

      setState(prev => ({
        ...prev,
        route: routeResponse.data,
        routePath: bulkRoutePaths,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to load route details',
        isLoading: false 
      }));
    }
  };

  const getBulkRoutePaths = async (route: RouteResponse): Promise<any> => {
    try {
      // Extract all station connections from the route
      const connections: Array<{fromStationId: string, toStationId: string}> = [];
      
      route.segments.forEach((segment, segmentIndex) => {
        console.log('Processing segment:', segment);
        console.log('Intermediate stations:', segment.intermediateStations);
        
        if (segment.intermediateStations && segment.intermediateStations.length > 0) {
          // For the first segment, use the overall route sourceStationId
          // For other segments, use the previous segment's destination
          let sourceStationId: string;
          if (segmentIndex === 0) {
            // First segment: use route's sourceStationId
            sourceStationId = (route as any).sourceStationId;
          } else {
            // Other segments: use previous segment's destination
            sourceStationId = route.segments[segmentIndex - 1].intermediateStations![route.segments[segmentIndex - 1].intermediateStations!.length - 1].stationId;
          }
          
          // Add connection from source station to first intermediate station
          const sourceToFirst = {
            fromStationId: sourceStationId,
            toStationId: segment.intermediateStations[0].stationId
          };
          console.log('Adding source to first intermediate:', sourceToFirst);
          connections.push(sourceToFirst);
          
          // Add connections between consecutive intermediate stations
          for (let i = 0; i < segment.intermediateStations.length - 1; i++) {
            const connection = {
              fromStationId: segment.intermediateStations[i].stationId,
              toStationId: segment.intermediateStations[i + 1].stationId
            };
            console.log('Adding intermediate connection:', connection);
            connections.push(connection);
          }
          
          // For the last segment, use the overall route destinationStationId
          // For other segments, use the next segment's source
          let destinationStationId: string;
          if (segmentIndex === route.segments.length - 1) {
            // Last segment: use route's destinationStationId
            destinationStationId = (route as any).destinationStationId;
          } else {
            // Other segments: use next segment's first intermediate
            destinationStationId = route.segments[segmentIndex + 1].intermediateStations![0].stationId;
          }
          
          // Add connection from last intermediate station to destination station
          const lastToDestination = {
            fromStationId: segment.intermediateStations[segment.intermediateStations.length - 1].stationId,
            toStationId: destinationStationId
          };
          console.log('Adding last intermediate to destination:', lastToDestination);
          connections.push(lastToDestination);
        } else {
          // If no intermediate stations, add direct connection from source to destination
          const directConnection = {
            fromStationId: (route as any).sourceStationId,
            toStationId: (route as any).destinationStationId
          };
          console.log('Adding direct connection:', directConnection);
          connections.push(directConnection);
        }
      });
      
      console.log('All connections:', connections);

      // Call bulk route paths API
      const bulkResponse = await routePathAPI.getBulkRoutePaths(connections);
      console.log('Bulk route paths response:', bulkResponse.data);
      return bulkResponse.data;
    } catch (error) {
      console.error('Error getting bulk route paths:', error);
      return { results: [] };
    }
  };

  // Helper function to sort route path results in correct order
  const getSortedRoutePathResults = () => {
    if (!state.routePath || !state.routePath.results || !state.route) {
      return [];
    }

    const results = state.routePath.results.filter(path => path.success === true);
    const sortedResults: any[] = [];

    console.log('=== DEBUGGING ROUTE PATH SORTING ===');
    console.log('All successful results:', results);
    console.log('Route segments:', state.route.segments);

    // Process each segment in order
    state.route.segments.forEach((segment, segmentIndex) => {
      console.log(`\n--- Processing Segment ${segmentIndex + 1} ---`);
      console.log('Segment:', segment);
      
      if (segment.intermediateStations && segment.intermediateStations.length > 0) {
        console.log('Intermediate stations:', segment.intermediateStations);
        
        // For the first segment, use the overall route sourceStationId
        // For other segments, use the previous segment's destination
        let sourceStationId: string;
        if (segmentIndex === 0) {
          // First segment: use route's sourceStationId
          sourceStationId = (state.route as any).sourceStationId;
        } else {
          // Other segments: use previous segment's destination
          sourceStationId = state.route!.segments[segmentIndex - 1].intermediateStations![state.route!.segments[segmentIndex - 1].intermediateStations!.length - 1].stationId;
        }
        
        // Add source to first intermediate
        const sourceToFirst = results.find(path => 
          path.fromStationId === sourceStationId && 
          path.toStationId === segment.intermediateStations![0].stationId
        );
        console.log('Looking for source to first:', {
          fromStationId: sourceStationId,
          toStationId: segment.intermediateStations![0].stationId,
          found: sourceToFirst
        });
        if (sourceToFirst) sortedResults.push(sourceToFirst);

        // Add intermediate to intermediate connections
        for (let i = 0; i < segment.intermediateStations.length - 1; i++) {
          const intermediateConnection = results.find(path => 
            path.fromStationId === segment.intermediateStations![i].stationId && 
            path.toStationId === segment.intermediateStations![i + 1].stationId
          );
          console.log(`Looking for intermediate ${i} to ${i+1}:`, {
            fromStationId: segment.intermediateStations![i].stationId,
            toStationId: segment.intermediateStations![i + 1].stationId,
            found: intermediateConnection
          });
          if (intermediateConnection) sortedResults.push(intermediateConnection);
        }

        // For the last segment, use the overall route destinationStationId
        // For other segments, use the next segment's source
        let destinationStationId: string;
        if (segmentIndex === state.route!.segments.length - 1) {
          // Last segment: use route's destinationStationId
          destinationStationId = (state.route as any).destinationStationId;
        } else {
          // Other segments: use next segment's first intermediate
          destinationStationId = state.route!.segments[segmentIndex + 1].intermediateStations![0].stationId;
        }

        // Add last intermediate to destination
        const lastToDestination = results.find(path => 
          path.fromStationId === segment.intermediateStations![segment.intermediateStations!.length - 1].stationId && 
          path.toStationId === destinationStationId
        );
        console.log('Looking for last to destination:', {
          fromStationId: segment.intermediateStations![segment.intermediateStations!.length - 1].stationId,
          toStationId: destinationStationId,
          found: lastToDestination
        });
        if (lastToDestination) sortedResults.push(lastToDestination);
      } else {
        // Direct connection
        const directConnection = results.find(path => 
          path.fromStationId === (state.route as any).sourceStationId && 
          path.toStationId === (state.route as any).destinationStationId
        );
        console.log('Looking for direct connection:', {
          fromStationId: (state.route as any).sourceStationId,
          toStationId: (state.route as any).destinationStationId,
          found: directConnection
        });
        if (directConnection) sortedResults.push(directConnection);
      }
    });

    console.log('Final sorted results:', sortedResults);
    console.log('=== END DEBUGGING ===\n');

    return sortedResults;
  };

  // Helper function to generate random coordinates 400m away from route path
  const generateRandomLocationNearPath = (routePaths: number[][]): { lat: number, lng: number } => {
    if (!routePaths || routePaths.length === 0) {
      return { lat: 0, lng: 0 };
    }

    // Pick a random point along the route path
    const randomIndex = Math.floor(Math.random() * routePaths.length);
    const basePoint = routePaths[randomIndex];
    const baseLng = basePoint[0]; // longitude
    const baseLat = basePoint[1]; // latitude

    // Generate random angle (0 to 2π)
    const randomAngle = Math.random() * 2 * Math.PI;
    
    // Distance in meters (400m)
    const distanceMeters = 400;
    
    // Convert distance to degrees (approximate)
    // 1 degree latitude ≈ 111,000 meters
    // 1 degree longitude ≈ 111,000 * cos(latitude) meters
    const latOffset = (distanceMeters / 111000) * Math.cos(randomAngle);
    const lngOffset = (distanceMeters / (111000 * Math.cos(baseLat * Math.PI / 180))) * Math.sin(randomAngle);

    return {
      lat: baseLat + latOffset,
      lng: baseLng + lngOffset
    };
  };

  // Helper function to get station name by ID
  const getStationNameById = (stationId: string): string => {
    if (!state.route) return stationId;
    
    // Check source and destination stations
    if (stationId === (state.route as any).sourceStationId) {
      return (state.route as any).sourceStationName || stationId;
    }
    if (stationId === (state.route as any).destinationStationId) {
      return (state.route as any).destinationStationName || stationId;
    }
    
    // Check intermediate stations in all segments
    for (const segment of state.route.segments) {
      // Check segment-level source and destination
      if (stationId === segment.fromStationId) {
        return segment.fromStationName || stationId;
      }
      if (stationId === segment.toStationId) {
        return segment.toStationName || stationId;
      }
      
      // Check intermediate stations
      if (segment.intermediateStations) {
        for (const station of segment.intermediateStations) {
          if (station.stationId === stationId) {
            return station.stationName;
          }
        }
      }
    }
    
    return stationId; // Fallback to ID if not found
  };

  const getAllStationsInOrder = () => {
    if (!state.route || !state.routePath) return [];
    
    // Handle the bulk API response structure  
    const routePaths = state.routePath?.results || [];
    console.log('Route paths results:', routePaths);
    console.log('Route paths type:', typeof routePaths);
    console.log('Route paths is array:', Array.isArray(routePaths));
    
    const stations: Array<{
      stationName: string;
      stationCode: string;
      lineName: string;
      lineColor: string;
      coordinates?: any[];
    }> = [];
    
    state.route.segments.forEach((segment) => {
      console.log('Processing segment for stations:', segment);
      
      // Add intermediate stations
      segment.intermediateStations?.forEach((intermediateStation, index) => {
        console.log('Processing intermediate station:', intermediateStation);
        
        const matchingPath = Array.isArray(routePaths) ? routePaths.find(path => 
          path.fromStationId === intermediateStation.stationId && 
          path.toStationId === segment.intermediateStations?.[index + 1]?.stationId &&
          path.success === true
        ) : undefined;
        
        console.log('Matching path for station:', intermediateStation.stationName, matchingPath);
        
        stations.push({
          stationName: intermediateStation.stationName,
          stationCode: intermediateStation.stationCode,
          lineName: segment.lineName || '',
          lineColor: segment.lineColor || '#000000',
          coordinates: matchingPath?.routePaths
        });
      });
    });
    
    console.log('Final stations:', stations);
    
    return stations;
  };

  // Helper function to get station coordinates by ID
  const getStationCoordinates = (stationId: string) => {
    const station = state.stations.find(s => s.id === stationId);
    if (station) {
      console.log(`Found station ${station.name} (${stationId}): lat=${station.lat}, lng=${station.lng}`);
      return { lat: station.lat, lng: station.lng };
    } else {
      console.warn(`Station not found for ID: ${stationId}`);
      return { lat: 0, lng: 0 };
    }
  };

  const startJourney = async () => {
    if (!state.route || !state.sourceStation || !state.destinationStation) {
      setState(prev => ({ ...prev, error: 'Please load route first' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      console.log('Creating journey data with intermediate stations...');
      console.log('Available stations:', state.stations.length);
      
      const intermediateStations = state.route.segments.flatMap(segment => 
        segment.intermediateStations?.map((station, index) => {
          const coordinates = getStationCoordinates(station.stationId);
          const stationData = {
            stationId: station.stationId,
            stationName: station.stationName,
            lineId: segment.lineId,
            lineName: segment.lineName || '',
            lineColor: segment.lineColor || '#00BFA5', // Default color if not available
            lat: coordinates.lat,
            lng: coordinates.lng,
            isCrossed: false,
            sequence: index + 1, // Calculate sequence based on position
          };
          console.log(`Intermediate station ${station.stationName}:`, stationData);
          return stationData;
        }) || []
      );

      const journeyData = {
        userId: 'test-user-123', // Test user ID
        journeyName: `Test Journey: ${state.sourceStation.name} to ${state.destinationStation.name}`,
        sourceStationId: state.sourceStation.id!,
        sourceStationName: state.sourceStation.name,
        sourceStationCode: state.sourceStation.stationCode,
        sourceStationLineId: state.route.segments[0]?.lineId || '',
        sourceStationLineName: state.route.segments[0]?.lineName || '',
        sourceStationLineColor: state.route.segments[0]?.lineColor || '#00BFA5',
        sourceStationLatitude: state.sourceStation.lat,
        sourceStationLongitude: state.sourceStation.lng,
        destinationStationId: state.destinationStation.id!,
        destinationStationName: state.destinationStation.name,
        destinationStationCode: state.destinationStation.stationCode,
        destinationStationLineId: state.route.segments[state.route.segments.length - 1]?.lineId || '',
        destinationStationLineName: state.route.segments[state.route.segments.length - 1]?.lineName || '',
        destinationStationLineColor: state.route.segments[state.route.segments.length - 1]?.lineColor || '#00BFA5',
        destinationStationLatitude: state.destinationStation.lat,
        destinationStationLongitude: state.destinationStation.lng,
        intermediateStations: intermediateStations,
      };

      console.log('Final journey data:', journeyData);

      const response = await journeyAPI.startJourney(journeyData);
      setState(prev => ({
        ...prev,
        journey: response.data,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to start journey',
        isLoading: false 
      }));
    }
  };

  const updateLocation = async () => {
    if (!state.journey || !testLocation.lat || !testLocation.lng) {
      setState(prev => ({ ...prev, error: 'Please enter valid coordinates and ensure journey is started' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Generate a unique eventId for this location update
      const eventId = `location_update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const response = await journeyAPI.updateLocation(
        state.journey.id!,
        eventId,
        parseFloat(testLocation.lat),
        parseFloat(testLocation.lng),
        testLocation.accuracy ? parseFloat(testLocation.accuracy) : undefined,
        testLocation.speed ? parseFloat(testLocation.speed) : undefined,
        Date.now() // Current timestamp
      );

      setState(prev => ({
        ...prev,
        journey: response.data,
        isLoading: false,
      }));
      
      // Store response for debugging
      setUpdateResponse({
        request: {
          journeyId: state.journey.id,
          eventId: eventId,
          lat: parseFloat(testLocation.lat),
          lng: parseFloat(testLocation.lng),
          accuracy: testLocation.accuracy ? parseFloat(testLocation.accuracy) : undefined,
          speed: testLocation.speed ? parseFloat(testLocation.speed) : undefined,
          timestamp: Date.now()
        },
        response: response.data,
        status: response.status,
        headers: response.headers
      });
      
      // Close location modal and show response modal
      setIsLocationModalOpen(false);
      setIsResponseModalOpen(true);
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to update location',
        isLoading: false 
      }));
    }
  };

  const stopJourney = async () => {
    if (!state.journey) {
      setState(prev => ({ ...prev, error: 'No active journey to stop' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await journeyAPI.stopJourney(state.journey.id!);
      setState(prev => ({
        ...prev,
        journey: response.data,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to stop journey',
        isLoading: false 
      }));
    }
  };

  const completeJourney = async () => {
    if (!state.journey) {
      setState(prev => ({ ...prev, error: 'No active journey to complete' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await journeyAPI.completeJourney(state.journey.id!);
      setState(prev => ({
        ...prev,
        journey: response.data,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: 'Failed to complete journey',
        isLoading: false 
      }));
    }
  };

  return (
    <div className="journey-testing">
      <div className="journey-testing-header">
        <h1>Journey Testing Dashboard</h1>
        <p>Test the complete journey flow from route planning to location updates</p>
      </div>

      {state.error && (
        <div className="error-message">
          <span>⚠️ {state.error}</span>
          <button onClick={() => setState(prev => ({ ...prev, error: null }))}>
            ✕
          </button>
        </div>
      )}

      <div className="journey-testing-content">
        {/* City Selection */}
        <div className="section">
          <h2>1. Select City</h2>
          <div className="city-grid">
            {state.cities.map(city => (
              <div
                key={city.id}
                className={`city-card ${state.selectedCity?.id === city.id ? 'selected' : ''}`}
                onClick={() => handleCitySelect(city)}
              >
                <h3>{city.name}</h3>
                <p>{city.country}</p>
                <span className="network-id">{city.networkId}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Station Selection */}
        {state.selectedCity && (
          <div className="section">
            <h2>2. Select Stations</h2>
            <div className="station-selection">
              <div className="station-group">
                <h3>Source Station</h3>
                <div className="station-list">
                  {state.stations.map(station => (
                    <div
                      key={station.id}
                      className={`station-item ${state.sourceStation?.id === station.id ? 'selected' : ''}`}
                      onClick={() => handleStationSelect(station, 'source')}
                    >
                      <div className="station-info">
                        <span className="station-name">{station.name}</span>
                        <span className="station-code">{station.stationCode}</span>
                        {station.isInterchange && <span className="interchange-badge">Interchange</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="station-group">
                <h3>Destination Station</h3>
                <div className="station-list">
                  {state.stations.map(station => (
                    <div
                      key={station.id}
                      className={`station-item ${state.destinationStation?.id === station.id ? 'selected' : ''}`}
                      onClick={() => handleStationSelect(station, 'destination')}
                    >
                      <div className="station-info">
                        <span className="station-name">{station.name}</span>
                        <span className="station-code">{station.stationCode}</span>
                        {station.isInterchange && <span className="interchange-badge">Interchange</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button 
              className="load-route-btn"
              onClick={loadRoute}
              disabled={!state.sourceStation || !state.destinationStation || state.isLoading}
            >
              {state.isLoading ? 'Loading...' : 'Load Route Details'}
            </button>
          </div>
        )}

        {/* Route Details */}
        {state.route && (
          <div className="section">
            <h2>3. Route Details</h2>
            <div className="route-info">
              <div className="route-summary">
                <h3>Route Summary</h3>
                <div className="route-stats">
                  <div className="stat">
                    <span className="label">Total Distance:</span>
                    <span className="value">{(state.route.totalDistance / 1000).toFixed(2)} km ({state.route.totalDistance} m)</span>
                  </div>
                  <div className="stat">
                    <span className="label">Total Duration:</span>
                    <span className="value">{state.route.totalDuration} minutes</span>
                  </div>
                  <div className="stat">
                    <span className="label">Total Stops:</span>
                    <span className="value">{state.route.segments.reduce((total, segment) => total + segment.numberOfStops, 0)}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Interchanges:</span>
                    <span className="value">{state.route.numberOfInterchanges}</span>
                  </div>
                </div>
              </div>

              <div className="route-segments">
                <h3>Route Segments</h3>
                {state.route.segments.map((segment, index) => (
                  <div key={index} className="route-segment">
                    <div 
                      className="line-indicator" 
                      style={{ backgroundColor: segment.lineColor || '#00BCD4' }}
                    >
                      {segment.lineCode || segment.lineName}
                    </div>
                    <div className="segment-info">
                      <div className="segment-route">
                        <span className="from">{segment.fromStationName}</span>
                        <span className="arrow">→</span>
                        <span className="to">{segment.toStationName}</span>
                      </div>
                      <div className="segment-details">
                        <span>{segment.distance.toFixed(2)} km</span>
                        <span>{segment.duration} min</span>
                        <span>{segment.numberOfStops} stops</span>
                      </div>
                      {segment.intermediateStations && segment.intermediateStations.length > 0 && (
                        <div className="intermediate-stations">
                          <span>Via: {segment.intermediateStations.map(s => s.stationName).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button 
              className="start-journey-btn"
              onClick={startJourney}
              disabled={state.isLoading}
            >
              {state.isLoading ? 'Starting...' : 'Start Journey'}
            </button>
          </div>
        )}

        {/* Route Timeline */}
        {state.route && (
          <div className="section">
            <h2>4. Route Timeline</h2>
            <div className="route-timeline">
              <div className="horizontal-timeline-container">
                {getAllStationsInOrder().map((station, index) => (
                  <div key={index} className="horizontal-timeline-item">
                    <div className="station-marker">
                      <div className="station-circle" style={{ backgroundColor: station.lineColor }}>
                        <span className="station-code">{station.stationCode}</span>
                      </div>
                      <div className="station-info">
                        <h4 className="station-name">{station.stationName}</h4>
                        <div className="station-details">
                          <span className="line-name" style={{ color: station.lineColor }}>
                            {station.lineName} Line
                          </span>
                          <span className="station-sequence">#{index + 1}</span>
                        </div>
                        {station.coordinates && station.coordinates.length > 0 && (
                          <div className="coordinates-info">
                            <span className="coord-count">{station.coordinates.length} coords</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {index < getAllStationsInOrder().length - 1 && (
                      <div className="connection-line" style={{ backgroundColor: station.lineColor }}>
                        <div className="line-arrow">→</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Route Coordinates Between Stations */}
        {state.routePath && state.routePath.results && state.routePath.results.length > 0 && (
          <div className="section">
            <h2>5. Route Coordinates Between Stations</h2>
            <div className="route-coordinates">
              <p className="coordinates-info">
                Detailed coordinates for each station-to-station segment. Use these coordinates to test different scenarios in the Journey Management section.
              </p>
              {!state.journey && (
                <div className="journey-required-notice">
                  <p>⚠️ <strong>Start a journey first</strong> to enable location testing with these coordinates.</p>
                </div>
              )}
              <div className="coordinates-sections">
                {getSortedRoutePathResults().map((path, pathIndex) => (
                  <div key={pathIndex} className="coordinate-section">
                    <div className="section-header">
                      <h3 className="section-title">
                        Segment {pathIndex + 1}: {getStationNameById(path.fromStationId)} → {getStationNameById(path.toStationId)}
                      </h3>
                      <div className="section-actions">
                        <div className="section-stats">
                          <span className="coord-count">{path.routePaths?.length || 0} coordinates</span>
                          <span className="path-status">{path.success ? 'Active' : 'Inactive'}</span>
                        </div>
                        {path.routePaths && path.routePaths.length > 0 && (
                          <button 
                            className="random-location-btn"
                            disabled={!state.journey}
                            onClick={() => {
                              const randomLocation = generateRandomLocationNearPath(path.routePaths);
                              setTestLocation({
                                lat: randomLocation.lat.toString(),
                                lng: randomLocation.lng.toString(),
                                accuracy: '5.0',
                                speed: '2.5'
                              });
                              setIsLocationModalOpen(true);
                            }}
                            title={!state.journey ? "Start a journey first to test location updates" : "Generate random location 400m away from this route segment"}
                          >
                            🎯 Random Location
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {path.routePaths && path.routePaths.length > 0 ? (
                      <div className="coordinates-grid">
                        <div className="coordinates-header">
                          <span className="header-index">#</span>
                          <span className="header-lat">Latitude</span>
                          <span className="header-lng">Longitude</span>
                          <span className="header-desc">Description</span>
                          <span className="header-action">Action</span>
                        </div>
                        <div className="coordinates-list">
                          {path.routePaths.map((coord: any, coordIndex: number) => (
                            <div key={coordIndex} className="coordinate-row">
                              <span className="coord-index">{coordIndex + 1}</span>
                                                      <span className="coord-lat">{coord[1]?.toFixed(6)}</span>
                        <span className="coord-lng">{coord[0]?.toFixed(6)}</span>
                              <span className="coord-desc">{coord.description || 'Route point'}</span>
                              <button 
                                className="use-coord-btn"
                                disabled={!state.journey}
                                onClick={() => {
                                  setTestLocation({
                                    lat: coord[1]?.toString() || '',
                                    lng: coord[0]?.toString() || '',
                                    accuracy: '5.0', // Default accuracy in meters
                                    speed: '2.5' // Default speed in m/s
                                  });
                                  setIsLocationModalOpen(true);
                                }}
                                title={!state.journey ? "Start a journey first to test location updates" : "Use this coordinate for testing"}
                              >
                                Use for Testing
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="no-coordinates">
                        <p>No coordinates available for this segment.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Journey Management */}
        {state.journey && (
          <div className="section">
            <h2>6. Journey Management</h2>
            <div className="journey-info journey-management-section">
              <div className="journey-status">
                <h3>Journey Status</h3>
                <div className="status-info">
                  <span className={`status-badge ${state.journey.status.toLowerCase()}`}>
                    {state.journey.status}
                  </span>
                  <span className="journey-id">ID: {state.journey.id}</span>
                </div>
                <div className="journey-details">
                  <p><strong>Route:</strong> {state.journey.sourceStationName} → {state.journey.destinationStationName || 'N/A'}</p>
                  <p><strong>Started:</strong> {state.journey.startTime ? new Date(state.journey.startTime).toLocaleString() : 'N/A'}</p>
                  {state.journey.currentLatitude && state.journey.currentLongitude && (
                    <p><strong>Current Location:</strong> {state.journey.currentLatitude.toFixed(6)}, {state.journey.currentLongitude.toFixed(6)}</p>
                  )}
                </div>
              </div>

              <div className="location-testing">
                <h3>Location Testing</h3>
                <div className="quick-actions">
                  <button 
                    className="quick-update-btn"
                    onClick={() => setIsLocationModalOpen(true)}
                    disabled={state.isLoading}
                  >
                    📍 Quick Update Location
                  </button>
                </div>
                <div className="location-inputs">
                  <div className="input-group">
                    <label>Latitude:</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={testLocation.lat}
                      onChange={(e) => setTestLocation(prev => ({ ...prev, lat: e.target.value }))}
                      placeholder="e.g., 28.6139"
                    />
                  </div>
                  <div className="input-group">
                    <label>Longitude:</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={testLocation.lng}
                      onChange={(e) => setTestLocation(prev => ({ ...prev, lng: e.target.value }))}
                      placeholder="e.g., 77.2090"
                    />
                  </div>
                  <div className="input-group">
                    <label>Accuracy (meters):</label>
                    <input
                      type="number"
                      step="0.1"
                      value={testLocation.accuracy}
                      onChange={(e) => setTestLocation(prev => ({ ...prev, accuracy: e.target.value }))}
                      placeholder="e.g., 5.0 (optional)"
                    />
                  </div>
                  <div className="input-group">
                    <label>Speed (m/s):</label>
                    <input
                      type="number"
                      step="0.1"
                      value={testLocation.speed}
                      onChange={(e) => setTestLocation(prev => ({ ...prev, speed: e.target.value }))}
                      placeholder="e.g., 2.5 (optional)"
                    />
                  </div>
                  <button 
                    className="update-location-btn"
                    onClick={updateLocation}
                    disabled={state.isLoading || !testLocation.lat || !testLocation.lng}
                  >
                    {state.isLoading ? 'Updating...' : 'Update Location'}
                  </button>
                </div>
              </div>

              <div className="journey-actions">
                <h3>Journey Actions</h3>
                <div className="action-buttons">
                  <button 
                    className="stop-journey-btn"
                    onClick={stopJourney}
                    disabled={state.isLoading || !['IN_TRANSIT', 'IN_TRANSIT_BAD_SIGNAL', 'ARRIVED'].includes(state.journey.status)}
                  >
                    Stop Journey
                  </button>
                  <button 
                    className="complete-journey-btn"
                    onClick={completeJourney}
                    disabled={state.isLoading || !['IN_TRANSIT', 'IN_TRANSIT_BAD_SIGNAL', 'ARRIVED'].includes(state.journey.status)}
                  >
                    Complete Journey
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Location Update Modal */}
      {isLocationModalOpen && (
        <div className="modal-overlay" onClick={() => setIsLocationModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📍 Update Journey Location</h2>
              <button 
                className="modal-close"
                onClick={() => setIsLocationModalOpen(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="location-inputs">
                <div className="input-group">
                  <label>Latitude:</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={testLocation.lat}
                    onChange={(e) => setTestLocation(prev => ({ ...prev, lat: e.target.value }))}
                    placeholder="e.g., 28.6139"
                  />
                </div>
                <div className="input-group">
                  <label>Longitude:</label>
                  <input
                    type="number"
                    step="0.000001"
                    value={testLocation.lng}
                    onChange={(e) => setTestLocation(prev => ({ ...prev, lng: e.target.value }))}
                    placeholder="e.g., 77.2090"
                  />
                </div>
                <div className="input-group">
                  <label>Accuracy (meters):</label>
                  <input
                    type="number"
                    step="0.1"
                    value={testLocation.accuracy}
                    onChange={(e) => setTestLocation(prev => ({ ...prev, accuracy: e.target.value }))}
                    placeholder="e.g., 5.0 (optional)"
                  />
                </div>
                <div className="input-group">
                  <label>Speed (m/s):</label>
                  <input
                    type="number"
                    step="0.1"
                    value={testLocation.speed}
                    onChange={(e) => setTestLocation(prev => ({ ...prev, speed: e.target.value }))}
                    placeholder="e.g., 2.5 (optional)"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="modal-cancel-btn"
                onClick={() => setIsLocationModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-update-btn"
                onClick={updateLocation}
                disabled={state.isLoading || !testLocation.lat || !testLocation.lng}
              >
                {state.isLoading ? 'Updating...' : 'Update Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Response Debug Modal */}
      {isResponseModalOpen && updateResponse && (
        <div className="modal-overlay" onClick={() => setIsResponseModalOpen(false)}>
          <div className="modal-content response-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔍 Update Location Response</h2>
              <button 
                className="modal-close"
                onClick={() => setIsResponseModalOpen(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="response-sections">
                <div className="response-section">
                  <h3>📥 Response Data</h3>
                  <div className="response-info">
                    <div className="response-status">
                      <span className="status-label">Status:</span>
                      <span className={`status-code ${updateResponse.status >= 200 && updateResponse.status < 300 ? 'success' : 'error'}`}>
                        {updateResponse.status}
                      </span>
                    </div>
                  </div>
                  <pre className="response-json">
                    {JSON.stringify(updateResponse.response, null, 2)}
                  </pre>
                </div>
                
                <div className="response-section">
                  <h3 
                    className="collapsible-header"
                    onClick={() => setIsRequestCollapsed(!isRequestCollapsed)}
                  >
                    📤 Request Data
                    <span className="collapse-icon">{isRequestCollapsed ? '▼' : '▲'}</span>
                  </h3>
                  {!isRequestCollapsed && (
                    <pre className="response-json">
                      {JSON.stringify(updateResponse.request, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="modal-cancel-btn"
                onClick={() => setIsResponseModalOpen(false)}
              >
                Close
              </button>
              <button 
                className="modal-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(updateResponse, null, 2));
                }}
              >
                📋 Copy Response
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyTesting;
